import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PlaylistService } from '@/services/playlist.service';
import type { Playlist } from '@/types';
import { PlaylistCard } from '@/components/playlists/PlaylistCard';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

export function DashboardPage() {
    const { user } = useAuth();
    const [recentPlaylists, setRecentPlaylists] = useState<Playlist[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!user) return;

        const fetchDashboardData = async () => {
            try {
                // Fetch all playlists and take top 4
                // Logic should ideally be in a service method like `getRecentPlaylists` using proper limits
                // but fetching all and slicing is fine for MVP without >100 playlists.
                const allPlaylists = await PlaylistService.getUserPlaylists(user.id);
                setRecentPlaylists(allPlaylists.slice(0, 4));
            } catch (error) {
                console.error('Failed to load dashboard data', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDashboardData();
    }, [user]);

    if (isLoading) return <LoadingSpinner fullScreen />;

    return (
        <div className="container-fluid p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0">Welcome back, {user?.name?.split(' ')[0]}!</h1>
                    <p className="text-muted small">Here's what's happening with your playlists.</p>
                </div>
                <Link to="/playlists" className="btn btn-outline-primary">
                    Manage Playlists
                </Link>
            </div>

            <section className="mb-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h2 className="h5 mb-0">Recent Playlists</h2>
                    {recentPlaylists.length > 0 && (
                        <Link to="/playlists" className="text-decoration-none small">View All</Link>
                    )}
                </div>

                {recentPlaylists.length === 0 ? (
                    <div className="card p-5 text-center bg-body-tertiary border-0 rounded-3">
                        <div className="mb-3">
                            <i className="bi bi-music-note-list display-4 text-muted opacity-25"></i>
                        </div>
                        <h5>No Playlists Found</h5>
                        <p className="text-muted">Start by creating your first playlist.</p>
                        <Link to="/playlists" className="btn btn-primary">Create Playlist</Link>
                    </div>
                ) : (
                    <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
                        {recentPlaylists.map(playlist => (
                            <div className="col" key={playlist.id}>
                                <PlaylistCard playlist={playlist} />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Placeholder for future "Activity Feed" or "Recommended" */}
            <section>
                <h2 className="h5 mb-3">Activity</h2>
                <div className="card border-0 shadow-sm p-4 text-muted text-center">
                    Coming soon: real-time activity feed from your collaborators.
                </div>
            </section>
        </div>
    );
}
